'use client';
/**
 * LES FRACTIONS DES LOTS — portions, cupules, aliquots d'un produit
 * fractionnable du catalogue (lot M3, 10/10/2026).
 *
 * Ce que l'écran montre : les SÉRIES en stock (un code imprimé, un compte,
 * dans l'ordre de prise — RQ, 05/10/2026 : « le nom, la date et le nombre
 * restant »), les fractions DEHORS avec leur horloge après sortie, ce qui est
 * à jeter, et les lots prêts à fractionner (le geste, lui, est sur le lot :
 * Stock › Réactifs & consommables).
 *
 * Tout ce qui se décide — dates limites, série à prendre d'abord, dérogation,
 * périmé — est calculé par le moteur (`src/fractions-materiel.ts`, règles des
 * aliquots). Cet écran affiche et transmet.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fmtDate, aujourdhuiIso } from '@/lib/meta';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed } from '@/lib/store';
import { card, inputStyle, Skeleton, I, useBusy, useConfirmation, FilterBar, Modale } from '@/components/ui';
import { Tableau } from '@/shared/Tableau';
import { lbl } from './ressources/commun';
import { cheminDe } from '@/lib/adresse';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { positionsPlanche } from '@/lib/planche-etiquettes';
import { imprimanteDuPoste, imprimerSurLePoste, type Detection } from '@/lib/zebra-poste';
import { ChoixSupport, LIBELLE_SUPPORT, planchePdf, telechargerZpl, type Support } from '@/components/screens/ressources/Aliquots';
import type { ConfigEtiquettesConf } from '@/components/aliquots-types';

type Serie = {
  codeSerie: string; codeLisible: string; lotRef: string; produitCode: string; produit: string; numeroLot: string; unite: string; uniteLibelle: string;
  volumeUl: number | null; temperature: string; dateFractionnement: string; restantes: number; sorties: number; utilisees: number; jetees: number; creees: number;
  limite: string; cause: string; causeLibelle: string; manques: string[]; perimee: boolean; lotRetire: boolean;
  /** Combien de fractions de la série ont déjà une étiquette — au-delà de zéro, réimprimer exige un motif. */
  etiquetees: number;
};
type Dehors = {
  documentId: string; codeSerie: string; codeLisible: string; lotRef: string; produit: string; unite: string; numero: number; nombreDeLaSerie: number;
  sortieLibelle: string; sortieParNom: string; limiteSortieLibelle: string; causeLibelle: string; manques: string[]; perimee: boolean;
};
type Etat = {
  aujourdhui: string;
  series: Serie[]; dehors: Dehors[];
  aJeter: { codeSerie: string; codeLisible: string; numero: number; documentId?: string; raison: string }[];
  lotsFractionnables: { documentId: string; ref: string; designation: string; produit: string; fractionnementLibelle: string; series: number }[];
  compte: { series: number; enStock: number; dehors: number };
  etiquettes: ConfigEtiquettesConf & { aConfirmer: string[]; zebraAdmise: { ok: boolean; raison?: string } };
  peutAgir: boolean;
};
type Reponse =
  | { issue: 'inconnu' | 'refus'; raison: string }
  | { issue: 'plus_ancienne'; raison: string; plusAncienne: { codeSerie: string; lotRef: string; limite: string } }
  | { issue: 'ok'; derogation: boolean; avertissements: string[]; fraction: Dehors };

const encart = (ton: 'rouge' | 'ambre' | 'vert'): React.CSSProperties => ({
  background: ton === 'rouge' ? 'var(--red-50)' : ton === 'ambre' ? 'var(--amber-50)' : 'var(--green-50)',
  borderInlineStart: `3px solid ${ton === 'rouge' ? 'var(--red-600)' : ton === 'ambre' ? 'var(--amber-500)' : 'var(--green-600)'}`,
  borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: 13, color: 'var(--text-body)', lineHeight: 1.5,
});

/** Sortir une fraction : on scanne le code de la série, ou on la choisit dans
 *  la liste ; le moteur répond une `issue` que l'écran affiche en grand. */
function Sortie({ serieChoisie, onFait, onAnnule }: { serieChoisie: Serie | null; onFait: () => void; onAnnule: () => void }) {
  const champ = useRef<HTMLInputElement>(null);
  const racine = useRef<HTMLDivElement>(null);
  const [saisie, setSaisie] = useState('');
  const [motif, setMotif] = useState('');
  const [reponse, setReponse] = useState<Reponse | null>(null);
  const { run, estOccupe } = useBusy();
  useEffect(() => { racine.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); champ.current?.focus(); }, []);

  const envoyer = () => void run('sortie', async () => {
    const corps = serieChoisie ? { mode: 'manuel', codeSerie: serieChoisie.codeSerie, motif } : { mode: 'scan', saisie, motif };
    const r = await fetchEcriture('/api/ged/ged/fractions/sortir', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps) });
    const d = await r.json().catch(() => null);
    if (!r.ok) { setReponse({ issue: 'refus', raison: d?.error?.message || 'Sortie refusée.' }); return; }
    setReponse(d);
    if (d?.issue === 'ok') onFait();
  });

  return (
    <div ref={racine} style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>
        {serieChoisie ? `Sortir une ${serieChoisie.uniteLibelle.replace(/s$/, '')} de la série ${serieChoisie.codeLisible}` : 'Sortir une fraction au scan'}
      </div>
      {serieChoisie ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{serieChoisie.produit} · lot {serieChoisie.numeroLot || serieChoisie.lotRef} · {serieChoisie.restantes} en stock — désignée à la main, le journal le dira.</div>
      ) : (
        <div>
          <label style={lbl} htmlFor="fr-scan">Code de la série (douchette ou clavier)</label>
          <input ref={champ} id="fr-scan" value={saisie} onChange={(e) => setSaisie(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); envoyer(); } }}
            placeholder="26 00417 3" style={{ ...inputStyle, fontFamily: 'var(--font-mono)', fontSize: 16, maxWidth: 260 }} autoComplete="off" />
        </div>
      )}
      {reponse && reponse.issue !== 'ok' && (
        <div role="alert" style={encart(reponse.issue === 'plus_ancienne' ? 'ambre' : 'rouge')}>
          <strong>{reponse.issue === 'plus_ancienne' ? 'Une série plus ancienne attend' : reponse.issue === 'inconnu' ? 'Code inconnu' : 'Refusé'}</strong> — {reponse.raison}
        </div>
      )}
      {reponse?.issue === 'plus_ancienne' && (
        <div>
          <label style={lbl} htmlFor="fr-motif">Motif pour prendre celle-ci quand même (consigné au journal)</label>
          <input id="fr-motif" value={motif} onChange={(e) => setMotif(e.target.value)} style={inputStyle} placeholder="5 caractères au moins" />
        </div>
      )}
      {reponse?.issue === 'ok' && (
        <div style={encart('vert')}>
          <strong>Sortie enregistrée</strong> — {reponse.fraction.produit}, série {reponse.fraction.codeLisible}, {reponse.fraction.unite} {reponse.fraction.numero}/{reponse.fraction.nombreDeLaSerie}.
          {reponse.fraction.limiteSortieLibelle !== '—' && <> À utiliser avant le <strong>{reponse.fraction.limiteSortieLibelle}</strong> ({reponse.fraction.causeLibelle}).</>}
          {reponse.derogation && <> Dérogation consignée.</>}
          {reponse.avertissements.map((a) => <div key={a} style={{ color: 'var(--amber-800)' }}>{a}</div>)}
        </div>
      )}
      <PiedActions taille="sm" annuler={{ libelle: reponse?.issue === 'ok' ? 'Fermer' : 'Annuler', onClick: onAnnule }}
        principale={reponse?.issue === 'ok'
          ? { libelle: 'Sortir une autre', onClick: () => { setReponse(null); setSaisie(''); setMotif(''); champ.current?.focus(); } }
          : { libelle: reponse?.issue === 'plus_ancienne' ? 'Confirmer avec ce motif' : 'Sortir', chargement: estOccupe('sortie'), disabled: estOccupe() || (reponse?.issue === 'plus_ancienne' && motif.trim().length < 5) || (!serieChoisie && !saisie.trim()), onClick: envoyer }} />
    </div>
  );
}

/* ── Étiquettes d'une série : la Zebra de ce poste, du réseau, un ZPL ou une planche ──
 * Le ZPL vient du moteur (le même pour les trois chemins) ; « Zebra de ce
 * poste » l'envoie à l'agent Zebra Browser Print installé sur l'ordinateur qui
 * édite (RQ, 11/10/2026). Toutes les fractions d'une série portent la même
 * étiquette (code de la série), sans rang : on découpe sans suivre quel morceau
 * va sur quel tube. On en imprime autant qu'on veut, jusqu'au nombre en stock
 * (planche entamée, étiquette abîmée). */
export function EtiquetterSerie({ serie, etat, onClose }: { serie: Serie; etat: Etat; onClose: () => void }) {
  const zebra = etat.etiquettes.zebraAdmise.ok;
  const [support, setSupport] = useState<Support>('poste');
  const [poste, setPoste] = useState<Detection | null>(null);
  useEffect(() => { let v = true; void imprimanteDuPoste().then((d) => { if (v) setPoste(d); }); return () => { v = false; }; }, []);
  const [caseDepart, setCaseDepart] = useState('1');
  const [nombreSaisi, setNombreSaisi] = useState(String(serie.restantes));
  const [motif, setMotif] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const g = etat.etiquettes.planche;
  const parPage = g.lignes * g.colonnes;
  const nombre = Number(nombreSaisi);
  const nombreValide = Number.isInteger(nombre) && nombre >= 1 && nombre <= serie.restantes;
  /* Le moteur imprime d'abord les fractions jamais étiquetées : un motif n'est
     exigé que si l'on en demande plus qu'il n'en reste de vierges. */
  const vierges = serie.restantes - serie.etiquetees;
  const reimpression = nombreValide && nombre > vierges;
  const motifManquant = reimpression && motif.trim().length < 5;

  const imprimer = async () => {
    setMessage(''); setEnvoi(true);
    try {
      const depart = Number(caseDepart);
      if (support === 'a4') {
        const pos = positionsPlanche(nombre, g, depart);
        if (pos.erreur) { setMessage(pos.erreur); return; }
      }
      const r = await fetchEcriture('/api/ged/ged/fractions/etiquettes', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codeSerie: serie.codeSerie, support, nombre, motif: reimpression ? motif.trim() || undefined : undefined }),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) { setMessage(d?.error?.message || 'Impression refusée.'); return; }
      if (support === 'poste') {
        const r2 = await imprimerSurLePoste(d.zpl);
        if (!r2.ok) { setMessage(r2.raison); return; }
        setMessage(`${d.etiquettes.length} étiquette(s) envoyée(s) à ${r2.imprimante} (ce poste).`);
      }
      if (support === 'zpl') telechargerZpl(d.zpl, `fractions-${serie.codeSerie}-${aujourdhuiIso()}.zpl`);
      if (support === 'a4') await planchePdf(d.etiquettes, g, depart, `planche-fractions-${serie.codeSerie}-${aujourdhuiIso()}.pdf`);
      if (support !== 'poste') setMessage(support === 'zebra' ? 'Envoyé à l’imprimante du réseau.' : 'Fichier prêt.');
      if (d.avertissements?.length) setMessage((m) => `${m} ${d.avertissements.join(' ')}`);
    } finally { setEnvoi(false); }
  };

  return (
    <Modale titre={`Étiquettes — série ${serie.codeLisible}`} sousTitre={`${serie.produit} · ${serie.restantes} ${serie.uniteLibelle} en stock`} largeur={560} onClose={onClose}
      pied={<PiedActions taille="sm" annuler={{ libelle: 'Fermer', onClick: onClose }}
        principale={{ libelle: LIBELLE_SUPPORT[support], chargement: envoi, disabled: envoi || !nombreValide || motifManquant || (support === 'poste' && !poste?.ok), title: !nombreValide ? `Un nombre de 1 à ${serie.restantes}` : motifManquant ? 'Saisissez le motif de réimpression' : support === 'poste' && !poste?.ok ? 'Aucune Zebra joignable sur ce poste' : undefined, onClick: () => void imprimer() }} />}>
      <div style={{ display: 'grid', gap: 12, fontSize: 13 }}>
        <ChoixSupport support={support} onChange={setSupport} poste={poste} zebraReseau={{ ok: zebra, raison: etat.etiquettes.zebraAdmise.raison }} a4Verifie={Boolean(g.verifieLe)} />
        <div>
          <label style={lbl} htmlFor="fr-etq-nombre">Combien d’étiquettes (de 1 à {serie.restantes})</label>
          <input id="fr-etq-nombre" type="number" min={1} max={serie.restantes} value={nombreSaisi} onChange={(e) => setNombreSaisi(e.target.value)} style={{ ...inputStyle, width: 90 }} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginInlineStart: 8 }}>
            {support !== 'a4' && <>Étiquette {etat.etiquettes.largeurMm} × {etat.etiquettes.hauteurMm} mm, {etat.etiquettes.dpi} dpi — </>}
            toutes identiques (code de la série), sans rang.
          </span>
        </div>
        {support === 'a4' && (
          <div>
            <label style={lbl} htmlFor="fr-etq-depart">Commencer à la case (planche entamée)</label>
            <input id="fr-etq-depart" type="number" min={1} max={parPage} value={caseDepart} onChange={(e) => setCaseDepart(e.target.value)} style={{ ...inputStyle, width: 90 }} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginInlineStart: 8 }}>1 = en haut à gauche, ligne par ligne ; {g.lignes} × {g.colonnes} = {parPage} par planche.</span>
          </div>
        )}
        {reimpression && (
          <div>
            <label style={lbl} htmlFor="fr-etq-motif">{serie.etiquetees} étiquette(s) déjà imprimée(s), {vierges} jamais — {nombre - vierges} seraient réimprimées : motif (consigné au journal du lot)</label>
            <input id="fr-etq-motif" value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="ex. étiquette décollée" style={{ ...inputStyle, width: '100%' }} />
          </div>
        )}
        {message && <div role="status" style={{ color: 'var(--text-body)' }}>{message}</div>}
      </div>
    </Modale>
  );
}

/** Les étiquettes d'une série qu'on vient de tirer, ouvertes depuis la page du
 *  lot (Stock › Réactifs & consommables) sans la quitter. La série et le
 *  réglage d'étiquetage se lisent au moteur : rien n'est recomposé ici. */
export function EtiquettesDeSerie({ codeSerie, onClose }: { codeSerie: string; onClose: () => void }) {
  const { showToast } = useGed();
  const [etat, setEtat] = useState<Etat | null>(null);
  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/fractions', { credentials: 'include' })
      .then(async (r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() as Promise<Etat>; })
      .then((d) => { if (vivant) setEtat(d); })
      .catch((e) => { if (vivant) { showToast(`Étiquettes indisponibles (${String(e?.message || e)}) — retrouvez la série dans Matériel › Fractionnement.`, 'erreur'); onClose(); } });
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeSerie]);
  const serie = etat?.series.find((s) => s.codeSerie === codeSerie);
  useEffect(() => {
    if (etat && !serie) { showToast('Série introuvable — retrouvez-la dans Matériel › Fractionnement.', 'erreur'); onClose(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etat, serie]);
  if (!etat || !serie) return null;
  return <EtiquetterSerie serie={serie} etat={etat} onClose={onClose} />;
}

export function Fractions() {
  const { showToast, go } = useGed();
  const [etat, setEtat] = useState<Etat | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [version, setVersion] = useState(0);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('stock');
  const [sortie, setSortie] = useState<{ ouverte: boolean; serie: Serie | null }>({ ouverte: false, serie: null });
  const [etiquetage, setEtiquetage] = useState<Serie | null>(null);
  const { run, estOccupe } = useBusy();
  const { demanderTexte, confirmation } = useConfirmation();

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/fractions', { credentials: 'include' })
      .then(async (r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (vivant) { setEtat(d); setErreur(''); } })
      .catch((e) => { if (vivant) setErreur(String(e?.message || e)); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [version]);
  const recharger = () => setVersion((n) => n + 1);

  const series = useMemo(() => {
    const tous = etat?.series || [];
    const base = filtre === 'stock' ? tous.filter((s) => s.restantes > 0 && !s.perimee && !s.lotRetire)
      : filtre === 'perimees' ? tous.filter((s) => s.perimee || s.lotRetire)
        : tous;
    const qq = q.trim().toLowerCase();
    return qq ? base.filter((s) => `${s.produit} ${s.numeroLot} ${s.lotRef} ${s.codeLisible} ${s.codeSerie}`.toLowerCase().includes(qq)) : base;
  }, [etat, filtre, q]);

  const jeterSerie = (s: Serie) => void run(`jeter-${s.codeSerie}`, async () => {
    const motif = await demanderTexte(`Jeter les ${s.restantes} ${s.uniteLibelle} en stock de la série ${s.codeLisible} (${s.produit}) ?`, { titre: 'Mettre au rebut', confirmer: 'Jeter', danger: true, min: 5, multiligne: true, placeholder: s.perimee ? `périmée : ${s.causeLibelle}` : 'Pourquoi ?' });
    if (motif === null) return;
    const r = await fetchEcriture('/api/ged/ged/fractions/jeter', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ codeSerie: s.codeSerie, motif }) });
    const d = await r.json().catch(() => null);
    if (!r.ok) { showToast(d?.error?.message || 'Rebut refusé.', 'erreur'); return; }
    showToast(`${d.jetees} fraction(s) jetée(s) — consigné au journal du lot ${s.lotRef}.`);
    recharger();
  });
  const jeterUne = (f: Dehors) => void run(`jeter-${f.documentId}`, async () => {
    const motif = await demanderTexte(`Jeter la ${f.unite} ${f.numero}/${f.nombreDeLaSerie} de la série ${f.codeLisible} (${f.produit}) ?`, { titre: 'Mettre au rebut', confirmer: 'Jeter', danger: true, min: 5, placeholder: f.perimee ? `périmée : ${f.causeLibelle}` : 'Pourquoi ?' });
    if (motif === null) return;
    const r = await fetchEcriture('/api/ged/ged/fractions/jeter', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ documentIds: [f.documentId], motif }) });
    const d = await r.json().catch(() => null);
    if (!r.ok) { showToast(d?.error?.message || 'Rebut refusé.', 'erreur'); return; }
    showToast('Fraction jetée — consigné au journal du lot.');
    recharger();
  });
  const utiliser = (f: Dehors) => void run(`util-${f.documentId}`, async () => {
    const r = await fetchEcriture('/api/ged/ged/fractions/utiliser', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ documentId: f.documentId }) });
    const d = await r.json().catch(() => null);
    if (!r.ok) { showToast(d?.error?.message || 'Refusé.', 'erreur'); return; }
    showToast(`${f.unite} ${f.numero}/${f.nombreDeLaSerie} déclarée utilisée.`);
    recharger();
  });

  if (chargement && !etat) return <div style={{ ...card, padding: 0 }}><Skeleton rows={5} /></div>;
  if (!etat) {
    return (
      <div style={{ ...card, padding: 16, display: 'grid', gap: 8, fontSize: 13, color: 'var(--red-600)' }}>
        Lecture des fractions impossible{erreur ? ` (${erreur})` : ''} — rien n’est affiché plutôt qu’un stock faux.
        <Bouton taille="sm" style={{ width: 'fit-content' }} onClick={recharger}>Réessayer</Bouton>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {confirmation}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {etat.peutAgir && (
          <Bouton variante="primaire" taille="sm" onClick={() => setSortie({ ouverte: true, serie: null })} aria-expanded={sortie.ouverte && !sortie.serie}>
            <I n="tube" s={13} />Sortir au scan
          </Bouton>
        )}
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {etat.compte.series} série(s) · {etat.compte.enStock} en stock · {etat.compte.dehors} dehors
          {etat.lotsFractionnables.length > 0 && <> · <a href={cheminDe('fourn')} onClick={(e) => { e.preventDefault(); go('fourn'); }} style={{ color: 'var(--color-primary-active)' }}>{etat.lotsFractionnables.length} lot(s) prêt(s) à fractionner</a> (Stock › Réactifs &amp; consommables)</>}
        </span>
      </div>

      {sortie.ouverte && <Sortie serieChoisie={sortie.serie} onFait={recharger} onAnnule={() => setSortie({ ouverte: false, serie: null })} />}
      {etiquetage && <EtiquetterSerie serie={etiquetage} etat={etat} onClose={() => { setEtiquetage(null); recharger(); }} />}

      {etat.aJeter.length > 0 && (
        <div role="alert" style={encart('rouge')}>
          <strong><I n="alert" s={13} /> À jeter — {etat.aJeter.length} fraction(s) hors stabilité</strong>
          <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
            {etat.aJeter.slice(0, 8).map((j) => <li key={`${j.codeSerie}-${j.numero}`}>série {j.codeLisible} n°{j.numero} — {j.raison}</li>)}
            {etat.aJeter.length > 8 && <li>… et {etat.aJeter.length - 8} autre(s).</li>}
          </ul>
        </div>
      )}

      {etat.dehors.length > 0 && (
        <div style={{ ...card, padding: 14, display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Dehors — en cours d’usage ({etat.dehors.length})</div>
          {etat.dehors.map((f) => (
            <div key={f.documentId} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, padding: '6px 0', borderTop: '1px solid var(--border-subtle)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{f.codeLisible}</span>
              <span>{f.produit} · {f.unite} {f.numero}/{f.nombreDeLaSerie} · lot {f.lotRef}</span>
              <span style={{ color: 'var(--text-muted)' }}>sortie le {f.sortieLibelle}{f.sortieParNom ? ` par ${f.sortieParNom}` : ''}</span>
              <span style={{ color: f.perimee ? 'var(--red-600)' : 'var(--text-body)', fontWeight: 600 }}>
                {f.limiteSortieLibelle === '—' ? `limite inconnue (${f.manques.join(', ')})` : `${f.perimee ? 'périmée depuis' : 'à utiliser avant'} le ${f.limiteSortieLibelle}`}
              </span>
              {etat.peutAgir && (
                <span style={{ display: 'flex', gap: 5, marginInlineStart: 'auto' }}>
                  <Bouton taille="sm" variante="contour" onClick={() => utiliser(f)} disabled={estOccupe()} chargement={estOccupe(`util-${f.documentId}`)}>Utilisée</Bouton>
                  <Bouton taille="sm" variante="danger" onClick={() => jeterUne(f)} disabled={estOccupe()} chargement={estOccupe(`jeter-${f.documentId}`)}>Jeter</Bouton>
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <FilterBar q={q} onQ={setQ} placeholder="Rechercher un produit, un lot, un code de série…"
        chips={[
          { k: 'stock', label: 'En stock', n: etat.series.filter((s) => s.restantes > 0 && !s.perimee && !s.lotRetire).length },
          { k: 'perimees', label: 'Périmées ou lot retiré', n: etat.series.filter((s) => s.perimee || s.lotRetire).length },
          { k: 'toutes', label: 'Toutes', n: etat.series.length },
        ]} actif={filtre} onChip={setFiltre} total={etat.series.length} affiches={series.length} onReset={() => { setQ(''); setFiltre('stock'); }} />

      <div style={{ ...card, overflow: 'hidden' }}>
        <Tableau
          id="fractions-series"
          ajusterLargeur
          niveau="standard"
          lignes={series}
          cleLigne={(s) => s.codeSerie}
          ligneProps={(s) => ({ style: s.perimee || s.lotRetire ? { background: 'var(--red-50)' } : undefined })}
          vide={{
            titre: etat.series.length === 0 ? 'Aucune série de fractions.' : 'Aucune série ne correspond à ces critères.',
            indice: etat.series.length === 0 ? 'Les fractions se tirent d’un lot accepté, depuis Stock › Réactifs & consommables (« Fractionner »), pour tout produit que le catalogue déclare fractionnable.' : 'Élargissez la recherche ou revenez à « En stock ».',
          }}
          colonnes={[
            { cle: 'code', libelle: 'Série', valeur: (s) => s.codeSerie, epinglee: true, obligatoire: true, export: true,
              rendu: (s) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)' }}>{s.codeLisible}</span> },
            { cle: 'produit', libelle: 'Produit · lot', valeur: (s) => s.produit, export: true,
              rendu: (s) => (
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>{s.produit}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{s.numeroLot ? `${s.numeroLot} · ` : ''}{s.lotRef}</span>
                </span>
              ) },
            { cle: 'date', libelle: 'Fractionné le', type: 'date', valeur: (s) => s.dateFractionnement, export: true, rendu: (s) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{fmtDate(s.dateFractionnement)}</span> },
            { cle: 'restantes', libelle: 'Restantes', type: 'nombre', valeur: (s) => s.restantes, export: true,
              rendu: (s) => <span style={{ fontWeight: 700, color: s.restantes ? 'var(--text-strong)' : 'var(--text-muted)' }}>{s.restantes} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>/ {s.creees} {s.uniteLibelle}{s.volumeUl ? ` · ${s.volumeUl} µL` : ''}</span></span> },
            { cle: 'limite', libelle: 'Limite', type: 'date', valeur: (s) => s.limite, export: true,
              rendu: (s) => (
                <span style={{ display: 'grid', gap: 2 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: s.perimee ? 'var(--red-600)' : undefined, fontWeight: s.perimee ? 700 : 400 }}>{s.limite ? fmtDate(s.limite) : '—'}</span>
                  <span style={{ fontSize: 11, color: s.manques.length ? 'var(--amber-800)' : 'var(--text-muted)' }}>
                    {s.lotRetire ? 'lot retiré' : s.perimee ? `périmée : ${s.causeLibelle}` : s.manques.length ? `incomplète : ${s.manques.join(', ')}` : s.causeLibelle}
                  </span>
                </span>
              ) },
          ]}
          actions={(s) => (etat.peutAgir ? (
            <span style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {s.restantes > 0 && !s.perimee && !s.lotRetire && (
                <Bouton variante="contour" taille="sm" onClick={() => setSortie({ ouverte: true, serie: s })} disabled={estOccupe()}>Sortir</Bouton>
              )}
              {s.restantes > 0 && !s.lotRetire && (
                <Bouton variante="contour" taille="sm" onClick={() => setEtiquetage(s)} disabled={estOccupe()}>Étiquettes</Bouton>
              )}
              {s.restantes > 0 && (
                <Bouton variante="danger" taille="sm" onClick={() => jeterSerie(s)} disabled={estOccupe()} chargement={estOccupe(`jeter-${s.codeSerie}`)}>Jeter</Bouton>
              )}
            </span>
          ) : null)}
        />
      </div>
    </div>
  );
}
